/* eslint-disable no-param-reassign */
import { Profile } from 'common/modules/ReactShared';
import { IStudentLicense } from 'common/modules/ReactShared';
import {
  IProfile,
  IProfilePreferences,
  IStudentTargetLanguage
} from 'common/modules/ReactShared';
import { tStudentIdentityProvider } from 'common/modules/ReactShared';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  messagesReconnect,
  tPromiseFromFunctionResolved
} from 'common/modules/ReactShared';
import { isNil, mapValues } from 'lodash';
import { tSharedAppDispatch, tSharedAppState } from '../../types';
import { ILanguageInstance, preferencesActions } from '../preferences';
import userSharedSelectors from '../selectors';
import { ILicenseTeamStudent } from '../../../api/profile/types';

type tProfileState = IProfile | null;
const initialProfileState: tProfileState = null;

const getProfile = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof Profile.profileApi.getProfile>,
  void
>('profile/getProfile_request', async () => {
  const profileData = await Profile.profileApi.getProfile();

  return profileData;
});

const updateProfile = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof Profile.profileApi.getProfile>,
  Parameters<typeof Profile.profileApi.updateProfile>[0],
  { dispatch: tSharedAppDispatch; rejectValue: string }
>('profile/updateProfile_request', async (profileData, { dispatch, rejectWithValue }) => {
  try {
    const newProfile = await Profile.profileApi.updateProfile(profileData);
    dispatch(actions.setProfile(newProfile));
    return newProfile;
  } catch (error) {
    return rejectWithValue('Validation error');
  }
});

const updateProfilePreferences = createAsyncThunk<
  unknown,
  Partial<IProfilePreferences>,
  {
    dispatch: tSharedAppDispatch;
    state: tSharedAppState;
  }
>(
  'profile/updateProfilePreferences_request',
  async (preferences, { dispatch, getState }) => {
    const profilePreferences = getState()?.shared?.user?.profile?.preferences;

    if (profilePreferences) {
      return dispatch(
        updateProfile({ preferences: { ...profilePreferences, ...preferences } })
      );
    }

    return null;
  }
);

const createStudentTargetLanguage = createAsyncThunk<
  tPromiseFromFunctionResolved<
    typeof Profile.profileApi.createStudentTargetLanguage
  >['studentTargetLanguage'],
  Parameters<typeof Profile.profileApi.createStudentTargetLanguage>[0]
>('profile/createStudentTargetLanguage_request', async (targetLanguageData) => {
  const newProfile = await Profile.profileApi.createStudentTargetLanguage(
    targetLanguageData
  );
  return newProfile.studentTargetLanguage;
});

const updateStudentTargetLanguage = createAsyncThunk<
  tPromiseFromFunctionResolved<
    typeof Profile.profileApi.updateStudentTargetLanguage
  >['studentTargetLanguage'],
  Parameters<typeof Profile.profileApi.updateStudentTargetLanguage>[0]
>('profile/updateStudentTargetLanguage_request', async (targetLanguageData) => {
  const newProfile = await Profile.profileApi.updateStudentTargetLanguage(
    targetLanguageData
  );
  return newProfile.studentTargetLanguage;
});

const selectStudentTargetLanguage = createAsyncThunk<
  unknown,
  Parameters<typeof createStudentTargetLanguage>[0],
  {
    dispatch: tSharedAppDispatch;
    state: tSharedAppState;
  }
>(
  'profile/selectStudentTargetLanguage',
  async (studentTargetLang, { dispatch, getState }) => {
    const profileState = getState().shared.user?.profile;

    if (profileState) {
      const { studentTargetLanguages } = profileState;
      const existingStudentTargetLanguage = studentTargetLanguages.find(
        (lang) => lang.languageId === studentTargetLang.languageId
      );
      if (existingStudentTargetLanguage) {
        return dispatch(
          updateStudentTargetLanguage({
            ...existingStudentTargetLanguage,
            ...studentTargetLang
          })
        );
      }
      return dispatch(createStudentTargetLanguage(studentTargetLang));
    }
    return null;
  }
);

const createStudentSupportLanguage = createAsyncThunk<
  tPromiseFromFunctionResolved<
    typeof Profile.profileApi.createStudentSupportLanguage
  >['studentSupportLanguage'],
  Parameters<typeof Profile.profileApi.createStudentSupportLanguage>[0]
>('profile/createStudentSupportLanguage_request', async (supportLanguage) => {
  const { studentSupportLanguage } =
    await Profile.profileApi.createStudentSupportLanguage(supportLanguage);

  return studentSupportLanguage;
});

const selectStudentSupportLanguage = createAsyncThunk<
  Promise<void>,
  {
    mainLanguageId: number;
    englishLanguageId?: number;
  },
  {
    dispatch: tSharedAppDispatch;
    state: tSharedAppState;
  }
>(
  'profile/selectStudentSupportLanguage',
  async ({ mainLanguageId, englishLanguageId }, { dispatch, getState }) => {
    const profileState = getState().shared.user.profile;

    if (profileState) {
      const { studentSupportLanguages } = profileState;

      // First: if main language is not yet created - create a new one
      const existingStudentSupportLanguage = studentSupportLanguages.find(
        (lang) => lang.languageId === mainLanguageId
      );

      if (!existingStudentSupportLanguage) {
        await dispatch(
          createStudentSupportLanguage({ languageId: mainLanguageId, native: true })
        );
      } else {
        await Profile.profileApi.updateStudentSupportLanguage({
          id: existingStudentSupportLanguage.id,
          url: existingStudentSupportLanguage.url,
          languageId: mainLanguageId,
          native: true
        });
      }

      // Second: if main language is not English - create new studentSupportLanguage
      if (englishLanguageId) {
        const existingEnglishSupportLanguage = studentSupportLanguages.find(
          (lang) => lang.languageId === englishLanguageId
        );

        if (!existingEnglishSupportLanguage) {
          await dispatch(
            createStudentSupportLanguage({ languageId: englishLanguageId, native: false })
          );
        }
      }
    }

    await dispatch(getProfile());
  }
);

const deleteProfileLanguage = createAsyncThunk<
  Promise<void>,
  { url: string },
  { dispatch: tSharedAppDispatch }
>('profile/deleteProfileLanguage_request', async ({ url }, { dispatch }) => {
  await Profile.profileApi.deleteProfileLanguageByUrl(url);
  await dispatch(getProfile());
});

const chooseSupportLangByCode = createAsyncThunk<
  Promise<void>,
  string,
  {
    dispatch: tSharedAppDispatch;
    state: tSharedAppState;
  }
>('profile/chooseSupportLangByCode', async (langCode, { dispatch, getState }) => {
  // get all langs array
  let { allLangs } = getState().shared.user.preferences;
  if (!allLangs.length) {
    await dispatch(preferencesActions.getSupportedLanguages());
  }
  allLangs = getState().shared.user.preferences.allLangs;

  // find lang in allLangs by langCode
  const lang = allLangs.find(({ code }) => code === langCode);
  if (!lang) {
    throw new Error(
      `Language with ${langCode} not found in the all supported languages array`
    );
  }

  await dispatch(selectStudentSupportLanguage({ mainLanguageId: lang.id }));
});

// Normalized, deduplicated base language codes from the browser, most
// preferred first ('en-US' -> 'en', legacy 'no' -> 'nb').
function getBrowserLanguageCodes(): string[] {
  if (typeof navigator === 'undefined') return [];

  const rawCodes = navigator.languages?.length
    ? [...navigator.languages]
    : [navigator.language].filter(Boolean);
  const normalized = rawCodes.map((rawCode) => {
    const baseCode = rawCode.toLowerCase().split('-')[0];
    return baseCode === 'no' ? 'nb' : baseCode;
  });

  return [...new Set(normalized)];
}

// Resolves to true only when a native support language was actually saved,
// so callers can fall back to the manual picker otherwise.
const setupSupportLanguagesFromBrowser = createAsyncThunk<
  boolean,
  void,
  {
    dispatch: tSharedAppDispatch;
    state: tSharedAppState;
  }
>('profile/setupSupportLanguagesFromBrowser', async (_, { dispatch, getState }) => {
  let { allLangs } = getState().shared.user.preferences;
  if (!allLangs.length) {
    await dispatch(preferencesActions.getSupportedLanguages());
    allLangs = getState().shared.user.preferences.allLangs;
  }

  const matchedLanguages = getBrowserLanguageCodes()
    .map((code) => allLangs.find((lang) => lang.code === code))
    .filter((lang): lang is ILanguageInstance => Boolean(lang));

  const english = allLangs.find((lang) => lang.code === 'en');
  const fallbackLanguages = english ? [english] : [];
  const [nativeLanguage, ...otherLanguages] = matchedLanguages.length
    ? matchedLanguages
    : fallbackLanguages;
  if (!nativeLanguage) return false;

  const existingLanguageIds = new Set(
    (getState().shared.user.profile?.studentSupportLanguages ?? []).map(
      (lang) => lang.languageId
    )
  );

  await Promise.all(
    otherLanguages
      .filter((lang) => !existingLanguageIds.has(lang.id))
      .map((lang) =>
        dispatch(createStudentSupportLanguage({ languageId: lang.id, native: false }))
      )
  );

  // The browser's primary language becomes the active (native) support
  // language; selectStudentSupportLanguage handles create-or-update and
  // finishes with a profile refresh, which completes this onboarding step.
  await dispatch(selectStudentSupportLanguage({ mainLanguageId: nativeLanguage.id }));

  // Inner thunk rejections are swallowed by dispatch, so verify against the
  // refreshed profile that the native language really got saved.
  const { profile } = getState().shared.user;
  return Boolean(profile?.nativeLanguageId || profile?.nativeStudentSupportLanguageId);
});

const updateProfileLocale = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof Profile.profileApi.getProfile>,
  IProfile['locale'],
  { dispatch: tSharedAppDispatch }
>('profile/updateStudentLocale_request', async (locale) => {
  return Profile.profileApi.updateProfile({ locale });
});

const disconnectFromSocialAccount = createAsyncThunk<
  void,
  { provider: tStudentIdentityProvider },
  {
    state: tSharedAppState;
    dispatch: tSharedAppDispatch;
  }
>(
  'profile/disconnectFromSocialAccount_request',
  async ({ provider }, { getState, dispatch }) => {
    const profile = userSharedSelectors.selectProfile(getState());
    const studentIdentity = profile?.studentIdentities?.find(
      (identity) => identity.provider === provider
    );
    if (!studentIdentity) return;

    await Profile.profileApi.disconnectStudentFromSocialAccount(studentIdentity.id);

    await dispatch(profileActions.getProfile());
  }
);

const changeCurrentLicence = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof Profile.profileApi.changeCurrentLicence>,
  IStudentLicense['id'],
  {
    dispatch: tSharedAppDispatch;
  }
>('profile/changeCurrentLicence_request', async (licenceId) => {
  const newCurrentLicense = await Profile.profileApi.changeCurrentLicence(licenceId);

  return newCurrentLicense;
});

const revokeReadAccessForSchool = createAsyncThunk<
  void,
  ILicenseTeamStudent['id'],
  {
    dispatch: tSharedAppDispatch;
  }
>('profile/revokeReadAccessForSchool_request', async (teamStudentId, { dispatch }) => {
  await Profile.profileApi.deleteStudentLicense(teamStudentId);

  await dispatch(profileActions.getProfile());
});

const deleteProfilePhoto = createAsyncThunk<
  void,
  void,
  {
    dispatch: tSharedAppDispatch;
  }
>('profile/deletePhoto_request', (_, { dispatch }) => {
  dispatch(updateProfile({ remove_avatar: true }));
});

const deleteProfile = createAsyncThunk<void, void, { dispatch: tSharedAppDispatch }>(
  'profile/deleteProfile_request',
  async (_args, { dispatch }) => {
    await Profile.profileApi.deleteProfile();

    dispatch(profileActions.removeProfileData());
  }
);

const restoreProfile = createAsyncThunk<void, void, { dispatch: tSharedAppDispatch }>(
  'profile/restoreProfile_request',
  async (_args, { dispatch }) => {
    await Profile.profileApi.restoreProfile();

    await dispatch(profileActions.getProfile());
  }
);

const exportData = createAsyncThunk<void, void, { dispatch: tSharedAppDispatch }>(
  'profile/exportData_request',
  async () => {
    await Profile.profileApi.exportData();
  }
);

const getExportRequests = createAsyncThunk<void, void, { dispatch: tSharedAppDispatch }>(
  'profile/getExportRequests_request',
  async () => {
    await Profile.profileApi.getExportRequests();
  }
);

const getProfileLearningProgress = createAsyncThunk<
  any,
  { from: string; to: string },
  { dispatch: tSharedAppDispatch; rejectValue: string }
>('profile/getProfileLearningProgress_request', async (params, { rejectWithValue }) => {
  try {
    return await Profile.profileApi.getProfileLearningProgress(params).catch((err) => {
      return rejectWithValue(err?.message || '');
    });
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.statusText ||
        'Error happened while trying to get Learning progress'
    );
  }
});

const getNewProfileLearningProgress = createAsyncThunk<
  any,
  {
    start_date?: string;
    end_date?: string;
  },
  { dispatch: tSharedAppDispatch; rejectValue: string }
>(
  'profile/getNewProfileLearningProgress_request',
  async (params, { rejectWithValue }) => {
    try {
      return await Profile.profileApi
        .getNewProfileLearningProgress(params)
        .catch((err) => {
          return rejectWithValue(err?.message || '');
        });
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.statusText ||
          'Error happened while trying to get New Learning progress'
      );
    }
  }
);

const getProfileStats = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof Profile.profileApi.getProfileStats>,
  {
    start_date?: string;
    end_date?: string;
  } | void,
  { dispatch: tSharedAppDispatch; rejectValue: string }
>('profile/getProfileStats_request', async (params, { rejectWithValue }) => {
  try {
    return await Profile.profileApi.getProfileStats(params || undefined).catch((err) => {
      return rejectWithValue(err?.message || '');
    });
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.statusText || 'Error happened while trying to get profile stats'
    );
  }
});

const profileSlice = createSlice({
  name: 'profile',
  initialState: initialProfileState as tProfileState,
  reducers: {
    setProfile: (
      _state,
      action: PayloadAction<
        tPromiseFromFunctionResolved<typeof Profile.profileApi.getProfile>
      >
    ) => {
      return {
        ...action.payload,
        preferences: convertKeysToBoolean(action.payload.preferences),
        studentTargetLanguages: removeZeroLevelFromTargetLangs(
          action.payload.studentTargetLanguages
        )
      };
    },
    removeProfileData: () => {
      return initialProfileState;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getProfile.fulfilled, (_, action) => {
      messagesReconnect(action.payload.id);

      return {
        ...action.payload,
        preferences: convertKeysToBoolean(action.payload.preferences),
        studentTargetLanguages: removeZeroLevelFromTargetLangs(
          action.payload.studentTargetLanguages
        )
      };
    });

    builder.addCase(changeCurrentLicence.fulfilled, (state, action) => {
      if (state) {
        state.activeLicense = action.payload;
      }
    });

    builder.addCase(updateProfileLocale.fulfilled, (_, action) => {
      return {
        ...action.payload,
        preferences: convertKeysToBoolean(action.payload.preferences),
        studentTargetLanguages: removeZeroLevelFromTargetLangs(
          action.payload.studentTargetLanguages
        )
      };
    });

    builder.addCase(createStudentTargetLanguage.fulfilled, (state, action) => {
      if (state) {
        state.studentTargetLanguages.push(
          removeZeroLevelFromTargetLangs([action.payload])[0]
        );
        if (action.payload.active) {
          state.activeStudentTargetLanguageId = action.payload.id;
        }
      }
    });

    builder.addCase(updateStudentTargetLanguage.fulfilled, (state, action) => {
      if (state) {
        const index = state.studentTargetLanguages.findIndex(
          (lang) => lang.id === action.payload.id
        );
        // eslint-disable-next-line prefer-destructuring
        state.studentTargetLanguages[index] = removeZeroLevelFromTargetLangs([
          action.payload
        ])[0];
        if (action.payload.active) {
          state.activeStudentTargetLanguageId = action.payload.id;
        }
      }
    });

    builder.addCase(getProfileLearningProgress.fulfilled, (state, action) => {
      if (state) {
        state.learningProgress = action.payload;
      }
    });

    builder.addCase(getNewProfileLearningProgress.fulfilled, (state, action) => {
      if (state) {
        state.newLearningProgress = action.payload.data;
      }
    });

    builder.addCase(getProfileStats.fulfilled, (state, action) => {
      if (state) {
        state.stats = action.payload.data;
      }
    });
  }
});

const { reducer, actions } = profileSlice;
export default reducer;
export const profileActions = {
  getProfile,
  updateProfile,
  updateProfilePreferences,
  selectStudentTargetLanguage,
  selectStudentSupportLanguage,
  updateProfileLocale,
  disconnectFromSocialAccount,
  revokeReadAccessForSchool,
  changeCurrentLicence,
  deleteProfilePhoto,
  chooseSupportLangByCode,
  setupSupportLanguagesFromBrowser,
  deleteProfileLanguage,
  deleteProfile,
  restoreProfile,
  exportData,
  getExportRequests,
  getProfileLearningProgress,
  getNewProfileLearningProgress,
  getProfileStats,
  ...actions
};

function convertKeysToBoolean(preferences: IProfilePreferences): IProfile['preferences'] {
  return mapValues(preferences, (value) => (isNil(value) ? true : value));
}

function removeZeroLevelFromTargetLangs(
  targetLanguages: Array<Profile.ProfileApiTypes.IStudentTargetLanguage>
): Array<IStudentTargetLanguage> {
  return targetLanguages.filter(
    (value) => value.level !== 'zero_level'
  ) as Array<IStudentTargetLanguage>;
}
